import { filter, forEach, isEmpty, isNumber, map, times } from 'es-toolkit/compat';
import { AsyncBuffer, byteLengthFromUrl } from 'hyparquet';
import pLimit from 'p-limit';

import { getParquetResourceId, measureParquetStage } from './instrumentation';
import { fetchParquetResponse, ParquetRequestError } from './requestError';

const rangeRequestLimit = pLimit(5);

export const defaultParquetRangeWindowSize = 1 << 23;

export async function byteLengthFromUrlWithGet(url: string) {
  return fetchParquetResponse(url, { method: 'GET' }).then((response) => {
    const length = response.headers.get('Content-Length');
    if (!length) throw new Error('Parquet response is missing its content length');
    return parseInt(length);
  });
}

const fetchUrlSlice = (url: string, start: number, end: number | undefined) =>
  rangeRequestLimit(async () => {
    const headers = new Headers();
    const endString = end === undefined ? '' : end - 1;
    headers.set('Range', `bytes=${start}-${endString}`);
    const resourceId = getParquetResourceId(url);
    const response = await fetchParquetResponse(url, { headers });
    if (!response.body) throw new Error(`Parquet byte range has no body for ${resourceId}`);
    return response.arrayBuffer();
  });

export async function asyncBufferFromUrlWithGetFallback(
  url: string,
  byteLength?: number,
  headUrl?: string
): Promise<AsyncBuffer> {
  if (!byteLength && headUrl) {
    byteLength = await byteLengthFromUrl(headUrl, undefined, fetchParquetResponse);
  }
  if (!byteLength) {
    try {
      byteLength = await byteLengthFromUrl(url, undefined, fetchParquetResponse);
      if (!isNumber(byteLength) || byteLength <= 1) {
        byteLength = await byteLengthFromUrlWithGet(url);
      }
    } catch {
      byteLength = await byteLengthFromUrlWithGet(url);
    }
  }

  return {
    byteLength,
    slice: async (start: number, end?: number) => fetchUrlSlice(url, start, end),
  };
}

export interface ParquetFileTail {
  byteLength: number;
  start: number;
  buffer: ArrayBuffer;
}

/**
 * Reads the file length and its last `tailSize` bytes in one suffix range request. Undefined when the server
 * ignores the range or does not expose `Content-Range`; callers then measure the length separately.
 */
export async function fetchParquetFileTail(url: string, tailSize: number): Promise<ParquetFileTail | undefined> {
  try {
    return await rangeRequestLimit(async () => {
      const headers = new Headers();
      headers.set('Range', `bytes=-${tailSize}`);
      const response = await fetchParquetResponse(url, { headers });
      const contentRange = response.headers.get('Content-Range')?.match(/^bytes (\d+)-(\d+)\/(\d+)$/);
      if (response.status !== 206 || !contentRange) {
        // Without a usable range response the body may be the whole file, so do not read it.
        void response.body?.cancel();
        return undefined;
      }
      return {
        byteLength: Number(contentRange[3]),
        start: Number(contentRange[1]),
        buffer: await response.arrayBuffer(),
      };
    });
  } catch (error) {
    if (error instanceof ParquetRequestError) throw error;
    return undefined;
  }
}

export function cachedAsyncBufferWithFixedWindows(
  { byteLength, slice }: AsyncBuffer,
  {
    minSize = defaultParquetRangeWindowSize,
    resourceId = 'parquet-unknown',
    thread = 'main',
    seed,
  }: {
    minSize?: number;
    resourceId?: string;
    thread?: 'main' | 'worker';
    // Bytes already fetched, such as the file tail; windows they fully cover are not fetched again.
    seed?: Omit<ParquetFileTail, 'byteLength'>;
  } = {}
): AsyncBuffer {
  const seedEnd = seed ? seed.start + seed.buffer.byteLength : 0;
  const seededSlice = (start: number, end: number) =>
    seed && start >= seed.start && end <= seedEnd ? seed.buffer.slice(start - seed.start, end - seed.start) : undefined;
  if (byteLength < minSize) {
    const buffer = seededSlice(0, byteLength) ?? slice(0, byteLength);
    return {
      byteLength,
      async slice(start: number, end: number) {
        return (await buffer).slice(start, end);
      },
    };
  }
  const numberOfCacheEntries = Math.ceil(byteLength / minSize);
  const cache = new Map<string, Promise<ArrayBuffer> | ArrayBuffer>();
  const allCacheAddresses = map(times(numberOfCacheEntries), (index) => {
    const start = index * minSize;
    const end = Math.min(start + minSize, byteLength);
    return { start, end };
  });
  forEach(allCacheAddresses, ({ start, end }) => {
    const seededWindow = seededSlice(start, end);
    if (seededWindow) cache.set(`${start}-${end}`, seededWindow);
  });
  return {
    byteLength,
    async slice(start: number, end: number | undefined): Promise<ArrayBuffer> {
      const relevantCacheAddresses = filter(allCacheAddresses, (address) => {
        return (
          (start >= address.start && start < address.end) ||
          (end !== undefined && end > address.start && end <= address.end) ||
          (start < address.start && (end === undefined || end > address.end))
        );
      });
      if (isEmpty(relevantCacheAddresses)) return new ArrayBuffer(0);

      const requestedSlices = await Promise.all(
        map(relevantCacheAddresses, async ({ start: sliceStart, end: sliceEnd }) => {
          const cacheKey = `${sliceStart}-${sliceEnd}`;
          const cachedSlice = cache.get(cacheKey);
          if (cachedSlice) return cachedSlice;
          if (sliceStart >= byteLength) return new ArrayBuffer(0);

          const requestedSlice = slice(sliceStart, Math.min(sliceEnd, byteLength));
          cache.set(cacheKey, requestedSlice);
          return requestedSlice;
        })
      );
      return measureParquetStage({ resourceId, stage: 'range-buffer-copy', thread }, () => {
        const totalLength = (end ?? byteLength) - start;
        const concatenatedBuffer = new ArrayBuffer(totalLength);
        const concatenatedBufferView = new Uint8Array(concatenatedBuffer);

        forEach(requestedSlices, (requestedSlice, sliceIndex) => {
          const { start: sliceStart, end: sliceEnd } = relevantCacheAddresses[sliceIndex];
          const windowCopyStart = Math.max(start, sliceStart);
          const windowCopyEnd = Math.min(end ?? byteLength, sliceEnd);
          const copyLength = windowCopyEnd - windowCopyStart;
          if (copyLength <= 0) return;

          concatenatedBufferView.set(
            new Uint8Array(requestedSlice, windowCopyStart - sliceStart, copyLength),
            windowCopyStart - start
          );
        });
        return concatenatedBuffer;
      });
    },
  };
}
